import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import lodashCompact from 'lodash/fp/compact';

import {
  hasUserAlreadyScheduledATour,
} from 'common/tourHelpers';
import {
  USER_ROLE_LIMITED_TOURS,
} from 'common/userRoles';
import {
  getShowingIsRequested,
  getShowingIsScheduled,
  getTourFromListing,
  isSunroomListing,
} from 'common/listingHelpers';
import {
  LISTING_DATA_SOURCE_SUNROOM,
} from 'common/listingConstants';
import { PARTNER_BILLING_PLAN_BASE } from 'common/partnerConstants';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import { formatPhoneNumber } from 'common/formatters';

import SrCalendarDaySlider from 'client/components/SrCalendarDaySlider';
import SrRequestShowingButton from 'client/components/SrRequestShowingButton';
import SrText from 'client/components/SrText';
import branch from 'client/core/branch';

@branch({
  currentUserRoles: ['currentUser', 'roles'],
  availableTourDates: ['availableTourDates'],
})
class SrListingTourSection extends Component {
  static propTypes = {
    listing: PropTypes.object.isRequired,
    tours: PropTypes.array,
    requestedFrom: PropTypes.string,
    availableTourDates: PropTypes.array,
    currentUserRoles: PropTypes.array.isRequired,
    partner: PropTypes.object,
  }

  constructor(props) {
    super(props);

    this.state = {
      selectedDate: null,
    };
  }

  _onSelectedDate = (selectedDate) => {
    this.setState({
      selectedDate,
    });
  }

  render() {
    const {
      listing,
      tours,
      requestedFrom,
      availableTourDates,
      currentUserRoles,
      partner,
      latestTourRequest,
    } = this.props;

    const {
      selectedDate,
    } = this.state;

    if (!availableTourDates) {
      return null;
    }

    const tourListing = getTourFromListing({
      listingId: listing.id,
      tours: tours,
    });
    const isShowingRequested = getShowingIsRequested({ listing, tours });
    const isShowingScheduled = getShowingIsScheduled({ listing, tours });
    const scheduledTourAlready = hasUserAlreadyScheduledATour({ tours });
    const isLimitedToursUser = (
      listing.dataSource !== LISTING_DATA_SOURCE_SUNROOM
      && currentUserRoles.includes(USER_ROLE_LIMITED_TOURS)
    );

    if (partner?.billingPlan === PARTNER_BILLING_PLAN_BASE && scheduledTourAlready) {
      return null;
    }

    const hasRecentTourRequest = latestTourRequest && moment(latestTourRequest.createdDate).isAfter(
      moment().subtract(7, 'days')
    );

    const {
      listingAgent,
      listingAgentCellPhone,
      listingAgentDirectWorkPhone,
      listingAgentEmail,
    } = listing;
    const listingAgentPhone = listingAgentCellPhone || listingAgentDirectWorkPhone;

    return (
      <div>
        {tourListing && tourListing.startDate && (isShowingRequested || isShowingScheduled)
          ? (
            <div className='addToTourPrompt'>
              <div className='titleContainer'>
                <span className='title'>your tour is requested</span>
              </div>
              <span>
                {'You have a tour requested for this home on '}
              </span>
              {tourListing && tourListing.startDate && (
                <span className='weight-medium'>
                  {momentWithAustinTimezone(tourListing.startDate).format('dddd, MMMM D [at] h:mm A')}.
                </span>
              )}
              <br />
              <span className='weight-medium'>We recommend you add 5 homes to your tour.</span>
              <button disabled className='button yellow disabled' type='button'>
                tour requested
              </button>
            </div>
          )
          : (
            <div>
              <div className='titleContainer'>
                <span className='title'>go tour this home</span>
              </div>
              {!scheduledTourAlready && !isLimitedToursUser && (
                <SrCalendarDaySlider
                  onChange={this._onSelectedDate}
                  isSunroomListing={isSunroomListing(listing)}
                />
              )}
              {isLimitedToursUser && (
                <div style={{ paddingBottom: '18px' }}>
                  {hasRecentTourRequest ? (
                    <SrText>
                      Sunroom emailed your tour request to the listing agent for this property
                      {listingAgent ? `: ${
                        lodashCompact([
                          listingAgent,
                          listingAgentEmail,
                          listingAgentPhone && formatPhoneNumber(listingAgentPhone),
                        ]).join(', ')
                      }.` : '.'}
                    </SrText>
                  ) : (
                    <SrText>
                      Click "see this home" to notify the owner that you're interested
                      in touring this property. They will receive your contact info so
                      they can reach out to you and set up a time to tour the property!
                    </SrText>
                  )}
                </div>
              )}
              <SrRequestShowingButton
                requestedFrom={requestedFrom}
                listing={listing}
                selectedDate={selectedDate}
                hasRecentTourRequest={hasRecentTourRequest}
              />
              <div className='helperTextContainer'>
                <span className='helperText'>{'It\'s free, with no commitment — cancel anytime'}</span>
              </div>
            </div>
          )
        }
      </div>
    );
  }
}

SrListingTourSection.displayName = 'SrListingTourSection';

export default SrListingTourSection;
